import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrowserQRCodeReader } from '@zxing/browser';
import { API_BASE } from './config';
import { createMonitorSocket } from '../socket/socket';
import BottomNavigation from './BottomNavigation';
import { MemberBrand, MemberIcon, MemberBadge, MemberPage, useMemberTheme } from './MemberUI';
import { resolveAvatarSource } from '../utils/avatar';
import { apiFetch } from '../utils/apiFetch';
import '../css/MonitorLayout.css';
import '../css/MonitorNeon.css';
const MONITOR_GUIDE_STEPS = [{
  title: 'เกมที่กำลังเล่น = ลงสนาม',
  text: 'เลือกแท็บ “กำลังเล่น” แล้วตรวจชื่อผู้เล่นและเลขสนามของคุณ',
  targetId: 'monitor-playing',
  action: 'ไปดูสนาม'
}, {
  title: 'ดูคิวรอลงสนาม',
  text: 'เลือกแท็บ “คิวรอ” ด้านบน ระบบจะเลื่อนไปยังคิวด้านล่าง',
  targetId: 'monitor-queue',
  action: 'ไปดูคิว'
}, {
  title: 'อัปเดตผลการแข่งขัน',
  text: 'เลื่อนไปส่วนผลการแข่งขัน แล้วเลือก T1 Win, Draw หรือ T2 Win',
  targetId: 'monitor-ended',
  action: 'ไปอัปเดตผล'
}];

/* =================================================
   HELPERS
================================================= */

const toArray = value => Array.isArray(value) ? value : Array.isArray(value?.data) ? value.data : [];
const urlBase64ToUint8Array = value => {
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(character => character.charCodeAt(0)));
};
const fetchJson = async path => {
  const response = await apiFetch(`${API_BASE}${path}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || `HTTP ${response.status}`);
  }
  return data;
};

/* =================================================
   MATCH TIME
================================================= */

export const isRecentlyStartedMatch = (startedAt, now) => {
  const started = new Date(startedAt || 0).getTime();
  return Number.isFinite(started) && started > 0 && now >= started && now - started < 120000;
};
const matchTimestamp = match => {
  const timestamp = new Date(match?.created_at || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};
const endedMatchTimestamp = match => {
  const timestamp = new Date(match?.ended_at || match?.end_time || match?.updated_at || match?.created_at || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};
const formatDuration = milliseconds => {
  const totalSeconds = Math.max(0, Math.floor(Number(milliseconds || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].filter((_, index) => hours > 0 || index > 0).map(value => String(value).padStart(2, '0')).join(':');
};
const formatMatchDateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '-';
  return new Intl.DateTimeFormat('th-TH', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
};
const matchResultLabel = result => ({
  team1_win: 'ทีม 1 ชนะ',
  team2_win: 'ทีม 2 ชนะ',
  draw: 'เสมอ'
})[String(result || '').toLowerCase()] || 'ยังไม่ระบุผล';

/* =================================================
   PLAYER COLOR
================================================= */

const playerColorStyle = player => {
  const source = String(player?.user_id ?? player?.name ?? 'player');
  const hash = [...source].reduce((total, character) => total * 31 + character.charCodeAt(0) >>> 0, 0);
  const hue = Math.round(hash * 137.508 % 360);
  return {
    '--monitor-player-ink': `hsl(${hue} 62% 31%)`,
    '--monitor-player-bg': `hsl(${hue} 86% 95%)`,
    '--monitor-player-border': `hsl(${hue} 62% 82%)`
  };
};

/* =================================================
   AVATAR URL
================================================= */

const getAvatarSource = avatarUrl => {
  return resolveAvatarSource(avatarUrl);
};

/* =================================================
   PLAYER BUTTON
================================================= */

function PlayerNameButton({
  player,
  onClick
}) {
  const playerName = player?.name || 'ไม่ระบุชื่อ';

  /*
   * รองรับหลายชื่อ field
   * เผื่อ Backend ส่งต่างกัน
   */
  const avatarUrl = player?.avatarUrl || player?.avatar_url || player?.avatar || '';
  const firstLetter = playerName.trim().charAt(0).toUpperCase() || '?';
  return <button type="button" className="monitor-player-link" style={playerColorStyle(player)} onClick={() => onClick(player)} title={`ดูประวัติการเล่นของ ${playerName}`}>

      {/* ================= AVATAR ================= */}

      <span className="monitor-player-avatar">

        {avatarUrl ? <img src={getAvatarSource(avatarUrl)} alt={`รูปของ ${playerName}`} onError={event => {
        event.currentTarget.style.display = 'none';
        const fallback = event.currentTarget.nextElementSibling;
        if (fallback) {
          fallback.style.display = 'grid';
        }
      }} /> : null}


        <span className="monitor-player-avatar-fallback" style={{
        display: avatarUrl ? 'none' : 'grid'
      }}>
          {firstLetter}
        </span>

      </span>


      {/* ================= NAME ================= */}

      <span className="monitor-player-copy">
        <span className="monitor-player-name">
          {playerName}
        </span>

        {player?.playingOtherMatchNo && <small className="monitor-player-busy">
            รอผู้เล่น • เกม #{player.playingOtherMatchNo}
            {player.playingOtherCourtNo ? ` • สนาม ${player.playingOtherCourtNo}` : ''}
          </small>}
      </span>

    </button>;
}

/* =================================================
   MATCH CARD
================================================= */

function MatchCard({
  match,
  tone,
  onPlayerClick,
  now,
  onEditResult,
  isSubmitting,
  isAdmin
}) {
  const players = Array.isArray(match?.players) ? match.players : [];
  const team1 = players.filter(player => Number(player.team) === 1);
  const team2 = players.filter(player => Number(player.team) === 2);
  const courtLabel = match.court_no ? `สนาม ${match.court_no}` : '-';
  const startTimestamp = new Date(match.start_time || (tone !== 'playing' ? match.created_at : null) || 0).getTime();
  const endTimestamp = endedMatchTimestamp(match);
  const duration = startTimestamp > 0 ? formatDuration((tone === 'ended' ? endTimestamp : now) - startTimestamp) : '--:--';
  const normalizedResult = String(match.result || '').toLowerCase();
  const editWindowOpen = true;
  const team1Won = tone === 'ended' && normalizedResult === 'team1_win';
  const team2Won = tone === 'ended' && normalizedResult === 'team2_win';
  const isDraw = tone === 'ended' && normalizedResult === 'draw';
  const isRecentlyStarted = tone === 'playing' && isRecentlyStartedMatch(match.start_time, now);
  const courtColorIndex = (Math.max(1, Number(match.court_no) || 1) - 1) % 6 + 1;
  return <article className={`monitor-match-card ${tone}${tone === 'playing' ? ` court-color-${courtColorIndex}` : ''}${isRecentlyStarted ? ' recently-started' : ''}${team1Won ? ' team1-winner' : ''}${team2Won ? ' team2-winner' : ''}${isDraw ? ' match-draw' : ''}`}>

      {/* ================= HEADER ================= */}

      <header>

        <span>

          {tone === 'playing' ? courtLabel : `เกม #${match.groupMatchNo || match.matchId}`}

        </span>


        <strong className={tone === 'playing' ? 'monitor-court-badge' : ''}>

          {tone === 'playing' ? isRecentlyStarted ? 'เพิ่งเริ่ม' : 'กำลังเล่น' : tone === 'ended' ? `จบแล้ว • ${courtLabel}` : 'รอสนาม'}


        </strong>

      </header>


      {/* ================= TEAM ================= */}

      <div className="monitor-match-teams">


        {/* ================= TEAM 1 ================= */}

        <div className={team1Won ? 'winner-team' : team2Won ? 'loser-team' : ''}>

          <small>
            T1
          </small>


          {team1.map(player => <PlayerNameButton key={player.user_id} player={player} onClick={onPlayerClick} />)}

        </div>


        {/* ================= VS ================= */}

        <b>
          VS
        </b>


        {/* ================= TEAM 2 ================= */}

        <div className={team2Won ? 'winner-team' : team1Won ? 'loser-team' : ''}>

          <small>
            T2
          </small>


          {team2.map(player => <PlayerNameButton key={player.user_id} player={player} onClick={onPlayerClick} />)}

        </div>

      </div>

      {tone === 'playing' && <div className="monitor-playing-duration" aria-label="เวลาแข่งขัน">
          <MemberIcon name="clock" size={20} /><span className="monitor-time-label">เวลาแข่งขัน</span><strong className="monitor-match-duration">{duration}</strong>
        </div>}

      {tone === 'ended' && <div className="monitor-ended-summary">
          <span><b>{matchResultLabel(match.result)}</b> • เวลาเล่น {duration}</span>
          <span className="monitor-ended-times">
            <MemberIcon name="clock" size={16} /> เริ่ม {formatMatchDateTime(match.start_time || match.created_at)}
            {' • '}สิ้นสุด {formatMatchDateTime(match.end_time || match.ended_at)}
          </span>
          <div className="monitor-result-actions" aria-label="อัปเดตผลการแข่งขัน">
            <button className={normalizedResult === 'team1_win' ? 'selected' : ''} disabled={isSubmitting || !editWindowOpen} onClick={() => onEditResult(match, 'TEAM1')}>T1 Win</button>
            <button className={normalizedResult === 'draw' ? 'selected' : ''} disabled={isSubmitting || !editWindowOpen} onClick={() => onEditResult(match, 'DRAW')}>Draw</button>
            <button className={normalizedResult === 'team2_win' ? 'selected' : ''} disabled={isSubmitting || !editWindowOpen} onClick={() => onEditResult(match, 'TEAM2')}>T2 Win</button>
          </div>
        </div>}

    </article>;
}

/* =================================================
   MATCH BOARD
================================================= */

function MatchBoard({
  matches,
  courts,
  onPlayerClick,
  onEditResult,
  submittingMatchId,
  isAdmin,
  activeTab = 'playing',
  searchQuery = ''
}) {
  // Keep the live clock inside the match board so the full Monitor page does
  // not rerender every second (groups, alerts, navigation and modals stay put).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  /* ================= PLAYING ================= */

  const playingMatches = useMemo(() => matches.filter(match => String(match.status).toLowerCase() === 'playing').sort((left, right) => Number(left.court_no || 999) - Number(right.court_no || 999)), [matches]);

  /* ================= QUEUE ================= */

  const queueMatches = useMemo(() => matches.filter(match => ['waiting', 'waiting_queue'].includes(String(match.status).toLowerCase())).sort((left, right) => Number(left.queue_no || 999999) - Number(right.queue_no || 999999) || matchTimestamp(left) - matchTimestamp(right) || Number(left.matchId) - Number(right.matchId)), [matches]);
  const playingCourtSlots = useMemo(() => {
    if (!courts?.length) return playingMatches.map(match => ({
      courtNo: Number(match.court_no || 0),
      match
    }));
    return [...courts].sort((left, right) => Number(left.court_no || 0) - Number(right.court_no || 0)).map(court => ({
      courtNo: Number(court.court_no),
      court,
      match: playingMatches.find(item => Number(item.court_no || item.courtNo) === Number(court.court_no)) || null
    })).filter(slot => !searchQuery.trim() || slot.match).sort((a, b) => Number(Boolean(b.match)) - Number(Boolean(a.match)) || a.courtNo - b.courtNo);
  }, [courts, playingMatches, searchQuery]);

  /* ================= ENDED ================= */

  const endedMatches = useMemo(() => matches.filter(match => ['ended', 'end', 'completed', 'finished'].includes(String(match.status || '').trim().toLowerCase())).sort((left, right) => endedMatchTimestamp(right) - endedMatchTimestamp(left) || Number(right.matchId || 0) - Number(left.matchId || 0)), [matches]);

  /* =================================================
     RENDER
  ================================================= */

  return <>

      {/* =================================================
         QUEUE + PLAY
       ================================================= */}

      <section className="monitor-match-board" hidden={activeTab === 'ended'} aria-label="เกมที่กำลังเล่นและคิวรอลงสนาม">


        {/* ================= QUEUE ================= */}

        <div className="monitor-match-column" id="monitor-queue" role={activeTab === 'queue' ? 'tabpanel' : 'region'} aria-labelledby={activeTab === 'queue' ? 'monitor-tab-queue' : undefined} aria-label={activeTab === 'playing' ? 'คิวถัดไป' : undefined}>

          <header className="
              monitor-match-heading
              queue-heading
            ">

            <span>

              <strong>
                {activeTab === 'playing' ? 'คิวถัดไป' : 'คิวรอแข่งขัน'}
              </strong>

              <small>
                Match ที่กำลังรอลงสนามตามลำดับคิว
              </small>

            </span>


            <b>
              {queueMatches.length} Match
            </b>

          </header>


          <div className="monitor-match-list">

            {queueMatches.length > 0 ? queueMatches.map(match => <MatchCard key={match.matchId} match={match} tone="waiting" onPlayerClick={onPlayerClick} now={now} />) : <div className="monitor-empty">

                    ยังไม่มี Match ในคิว

                  </div>}

          </div>

        </div>


        {/* ================= PLAY ================= */}

        <div className="monitor-match-column" id="monitor-playing" role="tabpanel" aria-labelledby="monitor-tab-playing" hidden={activeTab !== 'playing'}>

          <header className="
              monitor-match-heading
              play-heading
            ">

            <span>

              <strong>
                สนามแข่งขัน
              </strong>

              <small>
                Match ที่กำลังแข่งขันอยู่
              </small>

            </span>


            <b>
              {playingMatches.length} แมตช์
            </b>

          </header>


          <div className="monitor-match-list">

            {playingCourtSlots.length > 0 ? playingCourtSlots.map(slot => slot.match ? <MatchCard key={slot.match.matchId} match={slot.match} tone="playing" onPlayerClick={onPlayerClick} now={now} /> : <article className={`monitor-court-empty-card court-color-${(Math.max(1, Number(slot.courtNo) || 1) - 1) % 6 + 1}`} key={`court-${slot.courtNo}`}><svg className="nm-monitor-court-icon" viewBox="0 0 72 44" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M3 4h66v36H3zM3 11h66M3 33h66M8 4v36M64 4v36M36 1v42M3 22h66" /></svg><div><h3>สนาม {slot.courtNo}</h3><strong>ว่าง</strong><small>พร้อมรับแมตช์</small></div></article>) : <div className="monitor-empty">

                    ยังไม่มี Match ที่กำลังเล่น

                  </div>}

          </div>

        </div>

      </section>


      {/* =================================================
         ENDED
       ================================================= */}

      <section className="monitor-ended-section" id="monitor-ended" role="tabpanel" aria-labelledby="monitor-tab-ended" hidden={activeTab !== 'ended'}>

        <header className="
            monitor-match-heading
            ended-heading
          ">

          <span>

            <strong>
              เกมที่ผ่านมา
            </strong>

            <small>
              เลือกผลของ Match ที่เล่นจบแล้ว
            </small>

          </span>


          <b>
            {endedMatches.length} Match
          </b>

        </header>


        <div className="monitor-ended-list">

          {endedMatches.length > 0 ? endedMatches.map(match => <MatchCard key={match.matchId} match={match} tone="ended" onPlayerClick={onPlayerClick} now={now} onEditResult={onEditResult} isSubmitting={Number(submittingMatchId) === Number(match.matchId)} isAdmin={isAdmin} />) : <div className="monitor-empty">

                  ยังไม่มี Match ที่จบแล้ว

                </div>}

        </div>

      </section>

    </>;
}

/* =================================================
   MONITOR
================================================= */

function Monitor() {
  useMemberTheme();

  /* ================= ROUTER ================= */

  const {
    groupId: routeGroupId
  } = useParams();
  const navigate = useNavigate();
  const [selectedGroupId, setSelectedGroupId] = useState(() => Number(routeGroupId) || Number(localStorage.getItem('last_group_id')) || null);
  const loggedInUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  })();
  useEffect(() => {
    if (selectedGroupId) localStorage.setItem('last_group_id', String(selectedGroupId));
  }, [selectedGroupId]);

  // Keep old shared links working, but expose the clean canonical URL.
  useEffect(() => {
    if (routeGroupId) navigate('/monitor', {
      replace: true
    });
  }, [navigate, routeGroupId]);

  /* ================= STATE ================= */

  const [groups, setGroups] = useState([]);
  const [matches, setMatches] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [submittingMatchId, setSubmittingMatchId] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [, setMyGroupStatus] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const [liveConnected, setLiveConnected] = useState(false);
  const [boardTab, setBoardTab] = useState('playing');
  const [matchSearch, setMatchSearch] = useState('');
  const searchedMatches = useMemo(() => {
    const normalize = value => String(value ?? '').normalize('NFKD').toLocaleLowerCase('th').replace(/[\u0300-\u036f\u0e31\u0e34-\u0e3a\u0e47-\u0e4e\s#]/g, '');
    const query = normalize(matchSearch);
    if (!query) return matches;
    return matches.filter(match => normalize(match.matchId).includes(query) || (match.players || []).some(player => normalize(player.name || player.username || player.display_name).includes(query)));
  }, [matches, matchSearch]);
  const [headerAlertsOpen, setHeaderAlertsOpen] = useState(false);
  const [inAppAlert, setInAppAlert] = useState(null);
  const [showMonitorHelp, setShowMonitorHelp] = useState(false);
  const [showGuideBot, setShowGuideBot] = useState(false);
  const [guideStep, setGuideStep] = useState(0);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [waitAlertBusy, setWaitAlertBusy] = useState(false);
  const [waitAlertOpen, setWaitAlertOpen] = useState(false);
  const [waitAlertFeedback, setWaitAlertFeedback] = useState('');
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [qrScanMessage, setQrScanMessage] = useState('');
  const [qrScanError, setQrScanError] = useState(false);
  const audioContextRef = useRef(null);
  const notifiedMatchStatesRef = useRef(new Map());
  const qrVideoRef = useRef(null);
  const qrControlsRef = useRef(null);
  const requestVersionRef = useRef(0);
  useEffect(() => {
    if (!showMonitorHelp) return undefined;
    const handleKeyDown = event => {
      if (event.key === 'Escape') setShowMonitorHelp(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('monitor-help-open');
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('monitor-help-open');
    };
  }, [showMonitorHelp]);
  const openGuideTarget = targetId => {
    setBoardTab(targetId === 'monitor-ended' ? 'ended' : targetId === 'monitor-queue' ? 'queue' : 'playing');
    window.requestAnimationFrame(() => document.getElementById(targetId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    }));
  };
  const sendWaitAlert = async message => {
    if (!loggedInUser?.user_id || !localStorage.getItem('token')) return;
    if (!selectedGroupId) {
      setWaitAlertFeedback('กรุณาเลือกก๊วนก่อนแจ้งผู้จัด');
      return;
    }
    setWaitAlertBusy(true);
    setWaitAlertFeedback('');
    try {
      const response = await fetch(`${API_BASE}/api/match-planner/${Number(selectedGroupId)}/wait-alert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          message
        })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'แจ้งผู้จัดไม่สำเร็จ');
      setWaitAlertFeedback(result.alreadySent ? 'ส่งข้อความนี้ไว้แล้ว กรุณารอสักครู่' : 'ส่งข้อความถึงผู้จัดแล้ว');
      setWaitAlertOpen(false);
    } catch (waitError) {
      setWaitAlertFeedback(waitError.message || 'แจ้งผู้จัดไม่สำเร็จ');
    } finally {
      setWaitAlertBusy(false);
    }
  };
  const handleMonitorQrValue = useCallback(async rawValue => {
    try {
      setQrScanError(false);
      setQrScanMessage('กำลังตรวจสอบ QR...');
      const qrUrl = new URL(rawValue, window.location.origin);
      const code = qrUrl.searchParams.get('code');
      if (!/^\/join\/\d+$/i.test(qrUrl.pathname) || !code) {
        throw new Error('QR นี้ไม่ใช่ QR Check-in ของระบบ');
      }
      const qrGroupId = Number(qrUrl.pathname.split('/').filter(Boolean)[1]);
      const infoResponse = await apiFetch(`${API_BASE}/api/checkin/join/${qrGroupId}/info?code=${encodeURIComponent(code)}`);
      const groupData = await infoResponse.json().catch(() => ({}));
      if (!infoResponse.ok) throw new Error(groupData.message || 'QR Code ไม่ถูกต้อง');
      const statusResponse = await apiFetch(`${API_BASE}/api/checkin/status/${Number(loggedInUser.user_id)}`);
      const statusData = await statusResponse.json().catch(() => ({}));
      const isCurrentGroup = Number(statusData?.group_id) === qrGroupId;
      if (isCurrentGroup && String(statusData?.status || '').toUpperCase() === 'HOLD') {
        const response = await apiFetch(`${API_BASE}/api/checkin/join/${qrGroupId}/checkout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`
          }
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'เช็คอินไม่สำเร็จ');
        setQrScanMessage(data.message || `เช็คอิน ${groupData.Group_Name} สำเร็จ`);
      } else if (isCurrentGroup) {
        setQrScanMessage(`คุณเช็คอิน ${groupData.Group_Name} อยู่แล้ว`);
      } else {
        const response = await apiFetch(`${API_BASE}/api/checkin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: Number(loggedInUser.user_id),
            Group_Name: groupData.Group_Name
          })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'เช็คอินด้วย QR ไม่สำเร็จ');
        localStorage.setItem('last_group_id', String(qrGroupId));
        setSelectedGroupId(qrGroupId);
        setQrScanMessage(`สแกน QR และเช็คอิน ${groupData.Group_Name} สำเร็จ`);
      }
    } catch (scanError) {
      setQrScanError(true);
      setQrScanMessage(scanError.message || 'อ่าน QR ไม่สำเร็จ');
    }
  }, [loggedInUser?.user_id]);
  const openMonitorQrScanner = () => {
    if (!loggedInUser?.user_id || !localStorage.getItem('token')) {
      navigate(`/?redirect=${encodeURIComponent('/profile?scan=1')}`);
      return;
    }
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setQrScanError(true);
      setQrScanMessage('ไม่สามารถเปิดกล้องได้ กรุณาเปิดเว็บไซต์ผ่าน HTTPS และอนุญาตสิทธิ์กล้อง');
      setQrScannerOpen(true);
      return;
    }
    setQrScanError(false);
    setQrScanMessage('กำลังเปิดตัวสแกน QR...');
    setQrScannerOpen(true);
  };
  useEffect(() => {
    if (!qrScannerOpen || !qrVideoRef.current) return undefined;
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) return undefined;
    let cancelled = false;
    const reader = new BrowserQRCodeReader();
    const startScanner = async () => {
      try {
        const controls = await reader.decodeFromConstraints({ audio: false, video: { facingMode: { ideal: 'environment' } } }, qrVideoRef.current, (result, error, scannerControls) => {
          void error;
          if (!result || cancelled) return;
          cancelled = true;
          scannerControls.stop();
          setQrScannerOpen(false);
          handleMonitorQrValue(result.getText());
        });
        if (cancelled) {
          controls.stop();
          return;
        }
        qrControlsRef.current = controls;
        setQrScanMessage('หันกล้องหลังไปที่ QR Code ระบบจะอ่านให้อัตโนมัติ');
      } catch (cameraError) {
        if (cancelled) return;
        setQrScanError(true);
        setQrScanMessage(/permission|notallowed|denied/i.test(String(cameraError?.name || cameraError?.message || '')) ? 'กรุณาอนุญาตสิทธิ์กล้องให้เว็บไซต์แล้วลองใหม่' : 'เปิดตัวสแกน QR ไม่สำเร็จ กรุณาตรวจว่ากล้องไม่ได้ถูกแอปอื่นใช้งาน');
      }
    };
    startScanner();
    return () => {
      cancelled = true;
      qrControlsRef.current?.stop();
      qrControlsRef.current = null;
    };
  }, [handleMonitorQrValue, qrScannerOpen]);
  const unlockAlertAudio = useCallback(() => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!audioContextRef.current) audioContextRef.current = new AudioContextClass();
    if (audioContextRef.current.state === 'suspended') audioContextRef.current.resume().catch(() => {});
    return audioContextRef.current;
  }, []);
  const ringAlertBell = useCallback(() => {
    const context = unlockAlertAudio();
    if (!context || context.state !== 'running') return;
    const playTone = (frequency, delay, duration, volume) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, context.currentTime + delay);
      gain.gain.setValueAtTime(0.0001, context.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(volume, context.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + delay + duration);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(context.currentTime + delay);
      oscillator.stop(context.currentTime + delay + duration + 0.03);
    };
    playTone(880, 0, 0.34, 0.24);
    playTone(1175, 0.2, 0.48, 0.2);
    playTone(880, 0.72, 0.34, 0.2);
    playTone(1175, 0.92, 0.55, 0.18);
  }, [unlockAlertAudio]);
  const showMatchAlert = useCallback((match, status) => {
    const matchNo = match.group_match_no || match.groupMatchNo || match.id;
    const matchId = Number(match.id || match.matchId);
    const acknowledgementKey = `monitor_match_alert_ack_${Number(selectedGroupId)}_${Number(loggedInUser?.user_id)}_${matchId}_${status}`;
    if (localStorage.getItem(acknowledgementKey) === '1') return;
    const isPlaying = status === 'PLAYING';
    setInAppAlert({
      acknowledgementKey,
      type: isPlaying ? 'PLAY' : 'QUEUE',
      title: isPlaying ? '📣 ถึงเวลาลงสนาม' : '🏸 ใกล้ถึงคิวของคุณแล้ว',
      body: isPlaying ? `เกม #${matchNo} • สนาม ${match.court_no || match.courtNo || '-'}` : `เกม #${matchNo} อยู่ในคิวแล้ว กรุณาเตรียมตัว`
    });
    ringAlertBell();
    if ('vibrate' in navigator) navigator.vibrate(isPlaying ? [350, 120, 350, 120, 650] : [250, 100, 250]);
  }, [loggedInUser?.user_id, ringAlertBell, selectedGroupId]);
  useEffect(() => {
    const unlockOnce = () => unlockAlertAudio();
    window.addEventListener('pointerdown', unlockOnce, {
      once: true
    });
    window.addEventListener('keydown', unlockOnce, {
      once: true
    });
    return () => {
      window.removeEventListener('pointerdown', unlockOnce);
      window.removeEventListener('keydown', unlockOnce);
    };
  }, [unlockAlertAudio]);
  useEffect(() => {
    if (loading || !loggedInUser?.user_id) return;
    const userId = Number(loggedInUser.user_id);
    const current = new Map();
    const relevant = matches.filter(match => (Array.isArray(match.players) ? match.players : []).some(player => Number(player.user_id) === userId));
    relevant.forEach(match => {
      const status = String(match.status || '').trim().toUpperCase();
      const normalized = status === 'PLAYING' ? 'PLAYING' : ['WAITING', 'WAITING_QUEUE', 'QUEUE'].includes(status) ? 'QUEUE' : '';
      if (!normalized) return;
      const matchId = Number(match.id || match.matchId);
      current.set(matchId, normalized);
      const previous = notifiedMatchStatesRef.current.get(matchId);
      if (document.visibilityState === 'visible' && previous !== normalized) showMatchAlert(match, normalized);
    });
    notifiedMatchStatesRef.current = current;
  }, [loading, loggedInUser?.user_id, matches, showMatchAlert]);
  useEffect(() => {
    if (!inAppAlert) return undefined;
    const timer = window.setTimeout(() => setInAppAlert(null), 15000);
    return () => window.clearTimeout(timer);
  }, [inAppAlert]);
  useEffect(() => {
    if (!loggedInUser?.user_id) return;
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isIos && !isStandalone) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    let cancelled = false;
    navigator.serviceWorker.register('/push-sw.js').then(registration => registration.pushManager.getSubscription()).then(subscription => {
      if (cancelled) return;
      const enabled = Boolean(subscription);
      setPushEnabled(enabled);
      if (!enabled && Notification.permission !== 'denied' && sessionStorage.getItem('monitor_push_prompt_dismissed') !== '1') setShowPushPrompt(true);
    }).catch(() => {
      if (!cancelled) setPushEnabled(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loggedInUser?.user_id]);
  useEffect(() => {
    if (!loggedInUser?.user_id || !selectedGroupId) return;
    fetch(`${API_BASE}/api/checkin/status/${loggedInUser.user_id}`).then(response => response.json()).then(data => {
      if (Number(data.group_id) === Number(selectedGroupId)) setMyGroupStatus(String(data.status || '').toUpperCase());
    }).catch(() => {});
  }, [loggedInUser?.user_id, selectedGroupId]);
  const loadCourts = useCallback(async () => {
    if (!selectedGroupId) {
      setCourts([]);
      return;
    }
    try {
      const data = await fetchJson(`/api/courts/${selectedGroupId}/all`);
      setCourts(toArray(data));
    } catch (courtError) {
      console.error('[MONITOR COURTS]', courtError);
    }
  }, [selectedGroupId]);

  /* =================================================
     LOAD GROUP
  ================================================= */

  const loadGroups = useCallback(async () => {
    const data = await fetchJson('/api/groups');
    const nextGroups = toArray(data).filter(group => !['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').trim().toUpperCase()));
    setGroups(nextGroups);
    return nextGroups;
  }, []);

  /* =================================================
     LOAD MATCHES
  ================================================= */

  const loadMatches = useCallback(async ({
    silent = false
  } = {}) => {
    if (!selectedGroupId) {
      setMatches([]);
      setCourts([]);
      setLoading(false);
      return;
    }
    const requestVersion = ++requestVersionRef.current;
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const data = await fetchJson(`/api/game-match/group/${selectedGroupId}`);
      if (requestVersion !== requestVersionRef.current) {
        return;
      }
      setMatches(toArray(data));
      setError('');
    } catch (loadError) {
      if (requestVersion !== requestVersionRef.current) {
        return;
      }
      console.error('[MONITOR MATCH]', loadError);
      setError(loadError.message || 'โหลดข้อมูลคิวและสนามไม่สำเร็จ');
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [selectedGroupId]);

  /* =================================================
     LOAD GROUP
  ================================================= */

  useEffect(() => {
    loadGroups().catch(loadError => {
      setError(loadError.message || 'โหลดรายการ Group ไม่สำเร็จ');
      setLoading(false);
    });
  }, [loadGroups]);

  // Keep the waiting screen current even if a production proxy temporarily
  // misses a Socket.IO group event.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadGroups().catch(() => {});
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [loadGroups]);

  /* =================================================
     VALIDATE GROUP
  ================================================= */

  useEffect(() => {
    if (groups.length === 0) {
      setSelectedGroupId(null);
      return;
    }
    const selectedExists = groups.some(group => Number(group.id) === Number(selectedGroupId));
    if (!selectedExists) {
      setSelectedGroupId(Number(groups[0].id));
    }
  }, [groups, selectedGroupId]);

  /* =================================================
     LOAD MATCH
  ================================================= */

  useEffect(() => {
    loadMatches();
    loadCourts();
  }, [loadMatches, loadCourts]);

  /* =================================================
     FALLBACK 30 SEC
  ================================================= */

  useEffect(() => {
    if (!selectedGroupId) {
      return undefined;
    }
    const intervalId = window.setInterval(() => loadMatches({
      silent: true
    }), 30000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadMatches, selectedGroupId]);

  /* =================================================
     SOCKET
  ================================================= */

  useEffect(() => {
    const monitorSocket = createMonitorSocket();
    const joinGroup = () => {
      setLiveConnected(true);
      monitorSocket.emit('join-monitor-view');
      if (!selectedGroupId) return;
      monitorSocket.emit('join-group', selectedGroupId);
    };
    const refreshMatches = (payload = {}) => {
      const eventGroupId = payload.groupId ?? payload.group_id;
      if (eventGroupId && Number(eventGroupId) !== selectedGroupId) {
        return;
      }
      loadMatches({
        silent: true
      });
    };
    const matchEvents = ['match-generated', 'match-started', 'match-ended', 'match-result-updated', 'match-updated', 'queue-updated', 'match-cancelled', 'match-deleted',
    // เพิ่มไว้เผื่อ avatar update
    'player-avatar-updated'];
    monitorSocket.on('connect', joinGroup);
    const disconnected = () => setLiveConnected(false);
    monitorSocket.on('disconnect', disconnected);
    matchEvents.forEach(eventName => monitorSocket.on(eventName, refreshMatches));
    const courtEvents = ['court-status-updated', 'courts-added', 'courts-initialized', 'court-deleted'];
    courtEvents.forEach(eventName => monitorSocket.on(eventName, loadCourts));
    const refreshGroups = () => loadGroups().catch(() => {});
    const groupEvents = ['group-created', 'group-opened', 'group-closed', 'group-deleted'];
    groupEvents.forEach(eventName => {
      monitorSocket.on(eventName, refreshGroups);
    });
    if (monitorSocket.connected) {
      joinGroup();
    }
    return () => {
      monitorSocket.off('connect', joinGroup);
      monitorSocket.off('disconnect', disconnected);
      matchEvents.forEach(eventName => monitorSocket.off(eventName, refreshMatches));
      courtEvents.forEach(eventName => monitorSocket.off(eventName, loadCourts));
      groupEvents.forEach(eventName => {
        monitorSocket.off(eventName, refreshGroups);
      });
      monitorSocket.disconnect();
    };
  }, [loadGroups, loadCourts, loadMatches, selectedGroupId]);

  /* =================================================
     SELECT GROUP
  ================================================= */

  const submitMatchResult = useCallback(async (match, winnerTeam, editing = false) => {
    if (!match?.matchId || submittingMatchId !== null) return;
    const action = editing ? 'แก้ไขผล' : 'จบ Match';
    try {
      setSubmittingMatchId(match.matchId);
      const response = await fetch(editing ? `${API_BASE}/api/game-match/${match.matchId}/result` : `${API_BASE}/api/game-match/end`, {
        method: editing ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('token') ? {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          } : {})
        },
        body: JSON.stringify({
          matchId: match.matchId,
          winnerTeam
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || `${action}ไม่สำเร็จ`);
      await loadMatches({
        silent: true
      });
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmittingMatchId(null);
    }
  }, [loadMatches, submittingMatchId]);

  /* =================================================
     PLAYER HISTORY
  ================================================= */

  const openPlayerHistory = player => {
    if (!player?.user_id || !selectedGroupId) {
      return;
    }
    navigate(`/player-history/1/${player.user_id}`);
  };

  /* =================================================
     RANK
  ================================================= */

  const openRankTable = () => {
    navigate('/rank/');
  };
  const openMyProfile = () => {
    const userId = Number(loggedInUser?.user_id);
    if (!userId) return;
    navigate('/profile');
  };
  const openNotificationLogin = () => {
    const returnPath = '/monitor';
    navigate(`/?redirect=${encodeURIComponent(returnPath)}`);
  };
  const togglePushNotifications = async () => {
    if (pushBusy) return;
    const token = localStorage.getItem('device_token') || localStorage.getItem('token');
    if (!token) {
      setPushMessage('กรุณาเข้าสู่ระบบก่อนเปิดแจ้งเตือน');
      return;
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setPushMessage('แอปเปิดเว็บไซต์นี้ยังไม่รองรับการแจ้งเตือน');
      return;
    }
    try {
      setPushBusy(true);
      setPushMessage('');
      const registration = await navigator.serviceWorker.register('/push-sw.js');
      await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();
      if (pushEnabled || current) {
        await fetch(`${API_BASE}/api/push/unsubscribe`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            endpoint: current?.endpoint
          })
        });
        if (current) await current.unsubscribe();
        setPushEnabled(false);
        setPushMessage('ปิดแจ้งเตือนแล้ว');
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new Error('กรุณาอนุญาตการแจ้งเตือนในแอปเปิดเว็บไซต์');
      const keyResponse = await fetch(`${API_BASE}/api/push/public-key`);
      const keyData = await keyResponse.json().catch(() => ({}));
      if (!keyResponse.ok) throw new Error(keyData.message || 'โหลด Push Key ไม่สำเร็จ');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyData.publicKey)
      });
      const response = await fetch(`${API_BASE}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'เปิดแจ้งเตือนไม่สำเร็จ');
      setPushEnabled(true);
      setShowPushPrompt(false);
      setPushMessage('✓ เปิดแจ้งเตือนแล้ว');
      await fetch(`${API_BASE}/api/push/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          url: '/monitor'
        })
      });
    } catch (pushError) {
      setPushMessage(pushError.message);
    } finally {
      setPushBusy(false);
    }
  };
  const logoutMyself = async () => {
    if (checkingOut) return;
    const token = localStorage.getItem('device_token') || localStorage.getItem('token');
    if (!token) {
      setCheckoutMessage('กรุณาเข้าสู่ระบบก่อนออกจากระบบ');
      return;
    }
    if (!window.confirm('ต้องการออกจากระบบใช่หรือไม่?\nสถานะของคุณในทุกก๊วนจะเปลี่ยนเป็นพักชั่วคราว')) return;
    try {
      setCheckingOut(true);
      setCheckoutMessage('');
      const response = await fetch(`${API_BASE}/api/checkin/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'ออกจากระบบไม่สำเร็จ');
      localStorage.removeItem('token');
      localStorage.removeItem('device_token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('loginRedirect');
      setMyGroupStatus('HOLD');
      setCheckoutMessage('✓ ออกจากระบบแล้ว • ทุกก๊วนเปลี่ยนเป็นพักชั่วคราว');
    } catch (checkoutError) {
      setCheckoutMessage(checkoutError.message);
    } finally {
      setCheckingOut(false);
    }
  };

  /* =================================================
     RENDER
  ================================================= */

  return <MemberPage page="monitor" className="nm-monitor">
      <MemberBrand><MemberBadge tone={liveConnected ? 'live' : 'neutral'} role="status">{liveConnected ? 'อัปเดตสด' : 'กำลังเชื่อมต่อใหม่'}</MemberBadge></MemberBrand>

      {showPushPrompt && <div className="monitor-push-prompt" role="dialog" aria-modal="true" aria-labelledby="monitor-push-prompt-title">
          <div className="monitor-push-prompt-icon"><MemberIcon name="bell" /></div>
          <strong id="monitor-push-prompt-title">เปิดแจ้งเตือน Match แบบเรียลไทม์</strong>
          <p>รับแจ้งเตือนเมื่อถึงคิวและเมื่อถึงเวลาลงสนาม แม้ไม่ได้มองหน้าจออยู่</p>
          <div>
            <button type="button" className="monitor-push-prompt-later" onClick={() => {
          sessionStorage.setItem('monitor_push_prompt_dismissed', '1');
          setShowPushPrompt(false);
        }}>
              ไว้ภายหลัง
            </button>
            <button type="button" className="monitor-push-prompt-enable" disabled={pushBusy} onClick={togglePushNotifications}>
              {pushBusy ? 'กำลังเปิด…' : 'เปิดแจ้งเตือน'}
            </button>
          </div>
          {pushMessage && <small>{pushMessage}</small>}
        </div>}

      {inAppAlert && <div className={`monitor-inapp-alert monitor-inapp-${inAppAlert.type.toLowerCase()}`} role="alert" aria-live="assertive">
          <button type="button" className="monitor-inapp-close" onClick={() => setInAppAlert(null)} aria-label="ปิดแจ้งเตือน">×</button>
          <div className="monitor-inapp-bell"><MemberIcon name="bell" /></div>
          <strong>{inAppAlert.title}</strong>
          <span>{inAppAlert.body}</span>
          <button type="button" className="monitor-inapp-confirm" onClick={() => {
        if (inAppAlert.acknowledgementKey) localStorage.setItem(inAppAlert.acknowledgementKey, '1');
        setInAppAlert(null);
      }}>รับทราบ</button>
        </div>}


      {/* =================================================
         HERO
       ================================================= */}

      
<header className="nm-monitor-header">
  <h1 className="nm-monitor-sr">ดูคิวสนาม NAMAD Badminton</h1>
  <div className="nm-monitor-group-row">
    <label className="nm-monitor-group"><span className="nm-monitor-sr">เลือกก๊วน</span><select value={selectedGroupId || ''} onChange={event => setSelectedGroupId(Number(event.target.value))} disabled={!groups.length}><option value="" disabled>เลือกก๊วน</option>{groups.map(group => <option key={group.id} value={group.id}>{group.Group_Name}</option>)}</select><span className="nm-monitor-chevron" aria-hidden="true">⌄</span></label>
    <button type="button" className="nm-monitor-icon-button nm-monitor-refresh" aria-label={refreshing ? 'กำลังอัปเดตข้อมูล' : 'รีเฟรชข้อมูล'} disabled={!selectedGroupId || refreshing} aria-busy={refreshing || undefined} onClick={() => loadMatches({
          silent: true
        })}><MemberIcon name="refresh" size={25} /></button>
    <button type="button" className={'nm-monitor-icon-button nm-monitor-bell' + (headerAlertsOpen ? ' is-active' : '')} aria-label="การแจ้งเตือน" aria-expanded={headerAlertsOpen} aria-controls="monitor-notifications" onClick={() => setHeaderAlertsOpen(value => !value)}><MemberIcon name="bell" size={24} /></button>
  </div>
  {headerAlertsOpen && <section className="nm-monitor-notifications" id="monitor-notifications" aria-label="การแจ้งเตือน">
    <h2><MemberIcon name="bell" size={20} /> การแจ้งเตือน</h2>
    {loggedInUser?.user_id ? <>
      <button type="button" className="nm-monitor-push-toggle" disabled={pushBusy} aria-pressed={pushEnabled} onClick={togglePushNotifications}><MemberIcon name="bell" size={18} />{pushBusy ? 'กำลังดำเนินการ…' : pushEnabled ? 'ปิดแจ้งเตือนแมตช์' : 'เปิดแจ้งเตือนแมตช์'}</button>
      <button type="button" className="nm-monitor-wait-toggle" disabled={waitAlertBusy} aria-expanded={waitAlertOpen} onClick={() => {
            setWaitAlertFeedback('');
            setWaitAlertOpen(value => !value);
          }}>แจ้งผู้จัดว่ารอนาน</button>
      {waitAlertOpen && <div className="nm-monitor-wait-messages">{['อย่าลืมชั้นนน', 'ใกล้ถึงคิวชั้นยัง', 'หลับรอแล้วนะะ', 'น้ามาดสุดหล่อออออ'].map(message => <button type="button" key={message} disabled={waitAlertBusy} onClick={() => sendWaitAlert(message)}>{waitAlertBusy ? 'กำลังแจ้ง…' : message}</button>)}</div>}
    </> : <><p>เข้าสู่ระบบเพื่อรับแจ้งเตือนแมตช์และส่งข้อความถึงผู้จัด</p><button type="button" onClick={openNotificationLogin}>เข้าสู่ระบบ</button></>}
    {pushMessage && <p role="status">{pushMessage}</p>}{waitAlertFeedback && <p role="status">{waitAlertFeedback}</p>}
  </section>}
  <div className="nm-monitor-quick-links">
    <button type="button" onClick={openRankTable}><MemberIcon name="trophy" size={21} /><span>Ranking</span></button>
    <button type="button" onClick={() => setShowMonitorHelp(true)}><MemberIcon name="info" size={21} /><span>วิธีใช้งาน</span></button>
  </div>
  {checkoutMessage && <p className="monitor-checkout-message" role="status">{checkoutMessage}</p>}
  {showGuideBot && <section className="nm-monitor-guide" aria-label="คำแนะนำการใช้งาน"><header><h2>ผู้ช่วยหน้า Monitor</h2><button type="button" aria-label="ปิดคำแนะนำ" onClick={() => setShowGuideBot(false)}><MemberIcon name="close" /></button></header><small>ขั้นตอน {guideStep + 1}/{MONITOR_GUIDE_STEPS.length}</small><strong>{MONITOR_GUIDE_STEPS[guideStep].title}</strong><p>{MONITOR_GUIDE_STEPS[guideStep].text}</p><button type="button" onClick={() => openGuideTarget(MONITOR_GUIDE_STEPS[guideStep].targetId)}>{MONITOR_GUIDE_STEPS[guideStep].action}</button><footer><button type="button" disabled={guideStep === 0} onClick={() => setGuideStep(step => step - 1)}>ก่อนหน้า</button><button type="button" onClick={() => guideStep === MONITOR_GUIDE_STEPS.length - 1 ? setShowGuideBot(false) : setGuideStep(step => step + 1)}>{guideStep === MONITOR_GUIDE_STEPS.length - 1 ? 'เสร็จแล้ว' : 'ถัดไป'}</button></footer></section>}
  <div className="nm-monitor-search">
    <label htmlFor="monitor-match-search">ค้นหาผู้เล่นหรือแมตช์</label>
    <div><input id="monitor-match-search" type="search" value={matchSearch} onChange={event => setMatchSearch(event.target.value)} placeholder="ชื่อผู้เล่น หรือเลขแมตช์ เช่น 986" />{matchSearch && <button type="button" onClick={() => setMatchSearch('')} aria-label="ล้างการค้นหา">ล้าง</button>}</div>
    {matchSearch.trim() && <small role="status">พบ {searchedMatches.length} แมตช์ • เลือกแท็บเพื่อดูแต่ละสถานะ</small>}
  </div>
  <nav className="nm-monitor-tabs" role="tablist" aria-label="ส่วนการแข่งขัน">{[{
          value: 'playing',
          label: 'กำลังเล่น'
        }, {
          value: 'queue',
          label: 'คิวรอ'
        }, {
          value: 'ended',
          label: 'เกมที่ผ่านมา'
        }].map((tab, index, tabs) => <button key={tab.value} id={'monitor-tab-' + tab.value} type="button" role="tab" aria-selected={boardTab === tab.value} aria-controls={'monitor-' + tab.value} tabIndex={boardTab === tab.value ? 0 : -1} onClick={() => setBoardTab(tab.value)} onKeyDown={event => {
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
          if (!direction && event.key !== 'Home' && event.key !== 'End') return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + direction + tabs.length) % tabs.length;
          setBoardTab(tabs[next].value);
          event.currentTarget.parentElement.children[next].focus();
        }}>{tab.label}</button>)}</nav>
</header>

      {showMonitorHelp && <div className="monitor-help-overlay" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) setShowMonitorHelp(false);
    }}>
          <section className="monitor-help-dialog" role="dialog" aria-modal="true" aria-labelledby="monitor-help-title">
            <header className="monitor-help-header">
              <div>
                <span>คู่มือสำหรับสมาชิกใหม่</span>
                <h2 id="monitor-help-title">วิธีใช้ Live Match Monitor</h2>
              </div>
              <button type="button" onClick={() => setShowMonitorHelp(false)} aria-label="ปิดวิธีใช้งาน">
                ×
              </button>
            </header>

            <div className="monitor-help-grid">
              <article><MemberIcon name="court" /><div><strong>เลือกก๊วน</strong><p>เลือกก๊วนที่ต้องการดูข้อมูลสด</p></div></article>
              <article><span>↻</span><div><strong>อัปเดตข้อมูล</strong><p>ดึงข้อมูลการแข่งขันล่าสุดทันที</p></div></article>
              <article><MemberIcon name="trophy" /><div><strong>อันดับคะแนน</strong><p>ดู Rank และคะแนนของสมาชิก</p></div></article>
              <article><MemberIcon name="bell" /><div><strong>แจ้งเตือน</strong><p>เปิดเพื่อรับแจ้งเตือนเมื่อถึงคิว</p></div></article>
              <article><MemberIcon name="history" /><div><strong>ทางลัดการแข่งขัน</strong><p>ไปยังเกมที่เล่น คิว หรืออัปเดตผลการแข่งขัน</p></div></article>
              <article><MemberIcon name="profile" /><div><strong>รูปหรือชื่อผู้เล่น</strong><p>แตะเพื่อดูประวัติการเล่น</p></div></article>
              <article><MemberIcon name="clock" /><div><strong>เวลาบนสนาม</strong><p>แสดงเวลาตั้งแต่เริ่ม Match</p></div></article>
              <article><MemberIcon name="check" /><div><strong>ข้อมูลเรียลไทม์</strong><p>รายการจะอัปเดตเมื่อสถานะเปลี่ยน</p></div></article>
            </div>

            <div className="nm-monitor-help-actions">
              <button type="button" onClick={() => { setShowMonitorHelp(false); setShowGuideBot(true); }}>คำแนะนำทีละขั้น</button>
              <button type="button" onClick={loggedInUser?.user_id ? openMyProfile : openNotificationLogin}>{loggedInUser?.user_id ? 'ข้อมูลของฉัน' : 'เข้าสู่ระบบ'}</button>
              {loggedInUser?.user_id && <button type="button" disabled={checkingOut} onClick={logoutMyself}>{checkingOut ? 'กำลังออก…' : 'Checkout และออกจากระบบ'}</button>}
            </div>
            <button type="button" className="monitor-help-done" onClick={() => setShowMonitorHelp(false)}>
              เข้าใจแล้ว
            </button>
          </section>
        </div>}

      {/* =================================================
         ERROR
       ================================================= */}

      {error && <div className="monitor-error" role="alert">

          <MemberIcon name="info" /> {error}

        </div>}


      {/* =================================================
         CONTENT
       ================================================= */}

      {loading ? <div className="monitor-loading">

          กำลังโหลดข้อมูลคิวและสนาม...

        </div> : groups.length === 0 ? <section className="monitor-no-open-group" role="status">
          <MemberIcon name="court" size={40} />
          <h2>ยังไม่มีก๊วนเปิดอยู่</h2>
          <p>ก๊วนน้ามาดเริ่ม 19.00–00.00</p>
          <small>จัดทุกวันอังคารและวันพฤหัสบดี • เปิด 10 สนาม</small>
          <strong>แล้วพบกันนะครับ</strong>
        </section> : <MatchBoard matches={searchedMatches} searchQuery={matchSearch} courts={courts} activeTab={boardTab} onPlayerClick={openPlayerHistory} submittingMatchId={submittingMatchId} isAdmin={String(loggedInUser?.role || '').toLowerCase() === 'admin'} onEditResult={(match, winnerTeam) => submitMatchResult(match, winnerTeam, true)} />}

      {qrScanMessage && !qrScannerOpen && <button type="button" className={`monitor-scan-feedback${qrScanError ? ' is-error' : ''}`} onClick={() => setQrScanMessage('')}>
          <MemberIcon name={qrScanError ? "info" : "check"} /> {qrScanMessage}<small>แตะเพื่อปิด</small>
        </button>}

      {qrScannerOpen && <div className="monitor-qr-backdrop" role="dialog" aria-modal="true" aria-label="สแกน QR Check-in">
          <section className="monitor-qr-modal">
            <header><strong>สแกน QR Check-in</strong><button type="button" onClick={() => setQrScannerOpen(false)} aria-label="ปิดตัวสแกน">×</button></header>
            <div className="monitor-qr-camera"><video ref={qrVideoRef} autoPlay muted playsInline /><span aria-hidden="true" /></div>
            <p role={qrScanError ? 'alert' : 'status'} style={qrScanError ? { color: '#ffb4bd' } : undefined}>{qrScanMessage || 'วาง QR Code ให้อยู่ภายในกรอบ ระบบจะอ่านให้อัตโนมัติ'}</p>
          </section>
        </div>}

      <BottomNavigation scanLabel="สแกน QR" active="monitor" onMonitor={() => window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })} onMatches={() => navigate('/match-request')} onScan={openMonitorQrScanner} onReward={() => navigate('/rewards')} onProfile={() => navigate('/profile')} />

  </MemberPage>;
}
export default Monitor;
